import clsx from 'clsx';
import { User } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { BiLogOut } from 'react-icons/bi';
import { BsWindowSidebar } from 'react-icons/bs';
import { FaComments, FaPlug, FaTachometerAlt } from 'react-icons/fa';

import {
  LuBookOpen,
  LuGitPullRequest,
  LuKanban,
  LuMoon,
  LuRepeat,
  LuSettings,
  LuSparkles,
  LuSun,
  LuTerminal,
  LuTriangleAlert,
} from 'react-icons/lu';
import { NavLink, useNavigate } from 'react-router-dom';

import { fetchIntegrations } from '../shared/api/integration';
import { useTheme } from '../shared/contexts/Theme.context';
import { auth } from '../shared/firebase/firebase';
import SessionListSmall from './chat/Session.list.small';
import { WizardLauncher } from './dashboard/WizardLauncher';
import PipelineProgress from './PipelineProgress';

type SlidebarProps = {
  isOpen: boolean;
  onToggle: (isOpened: boolean) => void;
  /** px reserved above the sidebar by a global banner (e.g. the sandbox mode badge) */
  topOffset?: number;
};

const NavItem: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  isOpened: boolean;
  end?: boolean;
  external?: boolean;
  onClick?: () => void;
  warn?: boolean;
}> = ({ to, icon, label, isOpened, end, external, onClick, warn }) => (
  <NavLink
    to={to}
    end={end}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    title={!isOpened ? label : undefined}
    onClick={onClick}
    className={({ isActive }) =>
      clsx(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition hover:bg-base-200',
        'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary',
        isActive ? 'bg-base-200 font-medium text-base-content' : 'text-base-content/70',
      )
    }
  >
    {({ isActive }) => (
      <>
        <span
          className={clsx(
            'h-1.5 w-1.5 shrink-0 rounded-full transition-shadow',
            isActive ? 'bg-primary shadow-glow' : 'bg-transparent',
          )}
        />
        <span className="relative text-base opacity-80">
          {icon}
          {warn && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-warning" />}
        </span>
        {isOpened && <span className="truncate">{label}</span>}
        {isOpened && warn && <span className="ml-auto badge badge-warning badge-xs">!</span>}
      </>
    )}
  </NavLink>
);

const Slidebar: React.FC<SlidebarProps> = ({ isOpen, onToggle, topOffset = 0 }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState<User | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const isOpened = isOpen;
  const [isJiraConnected, setIsJiraConnected] = useState<boolean | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) setUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchIntegrations()
      .then((data) => {
        setIsJiraConnected(!!data?.isJiraConnected);
      })
      .catch(() => {
        // silently ignore — don't block the sidebar on a failed status check
      });
  }, []);

  const setOpened = (next: boolean) => {
    onToggle(next);
  };

  const handleLogout = () => navigate('/logout');

  const showJiraNudge = isJiraConnected === false;

  // Close sidebar on mobile after nav tap
  const handleNavClick = () => {
    if (window.innerWidth < 768) setOpened(false);
  };

  const brand = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-content shadow-glow">
        <span className="font-display text-sm font-bold">M</span>
      </div>
      {isOpened && (
        <div className="flex flex-col leading-tight">
          <span className="font-display font-bold tracking-tight">MFlow</span>
          <span className="text-[10px] uppercase tracking-widest text-base-content/50">Workspace</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <aside
        style={{ top: topOffset }}
        className={[
          'fixed left-0 bottom-0 z-40',
          'bg-base-100 text-base-content',
          'border-r border-base-300',
          // scoped to width/transform only — `top` is a one-time layout measurement,
          // not something that should visibly animate in
          'transition-[width,transform] duration-300',
          isOpened ? 'w-72 translate-x-0' : '-translate-x-full md:translate-x-0 w-72 md:w-20',
        ].join(' ')}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-base-100 border-b border-base-300">
          <div className={`flex items-center p-4 ${isOpened ? 'justify-between' : 'justify-center'}`}>
            {isOpened && brand}
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setOpened(!isOpened)}
              title={isOpened ? 'Collapse' : 'Expand'}
            >
              <BsWindowSidebar className="text-lg" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col" style={{ height: `calc(100vh - ${topOffset}px - 72px)` }}>
          {/* New project — always-available, frictionless wizard entry */}
          <div className="p-3 pb-0">
            <button
              onClick={() => setIsWizardOpen(true)}
              title={!isOpened ? 'New project from a spec' : undefined}
              className={clsx(
                'flex w-full items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-content shadow-glow transition hover:brightness-110',
                !isOpened && 'justify-center',
              )}
            >
              <LuSparkles className="text-base shrink-0" />
              {isOpened && <span>New project</span>}
            </button>
          </div>

          {/* Main nav */}
          <nav className="p-3 space-y-0.5">
            <NavItem
              to="/"
              end
              icon={<FaTachometerAlt />}
              label="Dashboard"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem to="/chat" icon={<FaComments />} label="Chat" isOpened={isOpened} onClick={handleNavClick} />
            <NavItem
              to="/standalone"
              icon={<LuKanban />}
              label="Standalone"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem
              to="/coding-agent"
              icon={<LuTerminal />}
              label="Coding agent"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem
              to="/git-reviewer"
              icon={<LuGitPullRequest />}
              label="Git Reviewer"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem
              to="/transfer"
              icon={<LuRepeat />}
              label="Transfers"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem
              to="/integrations"
              icon={<FaPlug />}
              label="Integrations"
              isOpened={isOpened}
              onClick={handleNavClick}
              warn={showJiraNudge}
            />
            <NavItem
              to="/settings"
              icon={<LuSettings />}
              label="Settings"
              isOpened={isOpened}
              onClick={handleNavClick}
            />
            <NavItem
              to="/docs"
              icon={<LuBookOpen />}
              label="Documentation"
              isOpened={isOpened}
              external
              onClick={handleNavClick}
            />
          </nav>

          {/* Jira nudge (expanded only) */}
          {showJiraNudge && isOpened && (
            <div className="px-3 pb-1">
              <button
                onClick={() => navigate('/integrations')}
                className="w-full rounded-lg text-left bg-warning/10 px-3 py-2.5 text-xs space-y-0.5 hover:bg-warning/15 transition"
              >
                <div className="flex items-center gap-1.5 font-medium text-warning">
                  <LuTriangleAlert size={13} />
                  Jira not connected
                </div>
                <div className="opacity-70 leading-snug">Connect Jira to sync projects and enable AI analytics.</div>
              </button>
            </div>
          )}

          {/* Divider */}
          <div className="px-3">
            <div className="divider my-2" />
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-3">
            {isOpened ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <div className="text-[10px] uppercase tracking-widest text-base-content/50">Recent sessions</div>
                  <button className="btn btn-ghost btn-xs" onClick={() => navigate('/chat')} title="Open chat page">
                    Open
                  </button>
                </div>

                <div className="rounded-lg bg-base-200 p-2">
                  <SessionListSmall />
                </div>
              </div>
            ) : (
              <div className="opacity-60 text-xs px-1 mt-2">Sessions</div>
            )}
          </div>

          {/* Pipeline progress */}
          <PipelineProgress isOpened={isOpened} />

          {/* Footer */}
          <div className="border-t border-base-300 bg-base-100 p-3 space-y-2">
            {user && (
              <div className={`flex items-center gap-3 px-2 py-1 ${!isOpened ? 'justify-center' : ''}`}>
                <div className="avatar placeholder shrink-0">
                  <div className="bg-neutral text-neutral-content rounded-full w-8">
                    {user.photoURL && !avatarError ? (
                      <img
                        src={user.photoURL}
                        alt="avatar"
                        className="rounded-full"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <span className="text-xs font-semibold">
                        {(user.displayName ?? user.email ?? '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                {isOpened && (
                  <div className="flex flex-col leading-tight min-w-0">
                    {user.displayName && <span className="text-sm font-medium truncate">{user.displayName}</span>}
                    {user.email && <span className="text-xs opacity-60 truncate">{user.email}</span>}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={toggleTheme}
              className={`btn btn-ghost btn-sm w-full ${!isOpened ? 'justify-center' : 'justify-start'}`}
              title={!isOpened ? 'Toggle theme' : undefined}
            >
              {theme === 'mflow-dark' ? (
                <LuSun className="text-lg opacity-80" />
              ) : (
                <LuMoon className="text-lg opacity-80" />
              )}
              {isOpened && <span className="ml-2">{theme === 'mflow-dark' ? 'Light mode' : 'Dark mode'}</span>}
            </button>

            <button
              onClick={handleLogout}
              className={`btn btn-ghost btn-sm w-full ${!isOpened ? 'justify-center' : 'justify-start'}`}
              title={!isOpened ? 'Logout' : undefined}
            >
              <BiLogOut className="text-lg opacity-80" />
              {isOpened && <span className="ml-2">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {isWizardOpen && <WizardLauncher onClose={() => setIsWizardOpen(false)} />}
    </>
  );
};

export default Slidebar;
